import { difference } from 'lodash-es';

export interface IMaintenanceModeUser {
  role?: string | string[] | null;
}

export interface IMaintenanceModeSettings {
  maintenance_mode?: unknown;
  maintenance_mode_exclude_roles?: unknown;
}

export function normalizeUserRoles(role: string | string[] | null | undefined): string[] {
  if (!role) {
    return [];
  }

  return Array.isArray(role) ? role : [role];
}

export function normalizeExcludeRoles(excludeRoles: unknown): string[] {
  if (!excludeRoles) {
    return [];
  }

  if (Array.isArray(excludeRoles)) {
    return excludeRoles.map(String);
  }

  if (typeof excludeRoles === 'string') {
    try {
      const parsed = JSON.parse(excludeRoles);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  return [];
}

export function coerceMaintenanceModeEnabled(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return false;
}

/**
 * Returns true when the given user should be shown the maintenance page.
 */
export const MAINTENANCE_SETTING_KEYS = ['maintenance_mode', 'maintenance_mode_bulletin', 'maintenance_mode_exclude_roles'] as const;

export type MaintenanceSettingKey = (typeof MAINTENANCE_SETTING_KEYS)[number];

export function normalizeMaintenanceSettingValue(key: MaintenanceSettingKey, value: unknown): unknown {
  if (key === 'maintenance_mode_bulletin') {
    if (value == null || value === '') {
      return null;
    }

    return String(value).trim();
  }

  if (key === 'maintenance_mode_exclude_roles') {
    return normalizeExcludeRoles(value);
  }

  if (key === 'maintenance_mode') {
    return coerceMaintenanceModeEnabled(value);
  }

  return value;
}

export function normalizeConfigSettingsFromApi<T extends Record<string, unknown>>(settings: T): T {
  const normalized = { ...settings };

  for (const key of MAINTENANCE_SETTING_KEYS) {
    if (key in normalized) {
      (normalized as Record<string, unknown>)[key] = normalizeMaintenanceSettingValue(key, normalized[key]);
    }
  }

  return normalized;
}

export function shouldShowMaintenancePage(user: IMaintenanceModeUser | null | undefined, settings: IMaintenanceModeSettings): boolean {
  if (!coerceMaintenanceModeEnabled(settings.maintenance_mode)) {
    return false;
  }

  const excludeRoles = normalizeExcludeRoles(settings.maintenance_mode_exclude_roles);

  if (user && excludeRoles.length > 0) {
    const userRoles = normalizeUserRoles(user.role);
    const unmatchedExcludeRoles = difference(excludeRoles, userRoles);
    return !(unmatchedExcludeRoles.length < excludeRoles.length);
  }

  return true;
}
